import {
  Button,
  Combobox,
  Dropdown,
  Field,
  Input,
  Option,
  Switch,
  Tooltip,
  makeStyles,
  mergeClasses,
  tokens,
} from '@fluentui/react-components';
import {
  ArrowDown16Regular,
  ArrowUp16Regular,
  Checkmark16Regular,
  ChevronRight12Regular,
  Dismiss16Regular,
  Prohibited16Regular,
} from '@fluentui/react-icons';
import { useMemo, useState } from 'react';
import type { Device, PolicyAction, PolicyEndpoint, PolicyProtocol, PolicyRule } from '../../api/types';
import { networksFromDevices } from '../../lib/networks';
import { vt } from '../../theme';
import { formatEndpoint, hitsText, matchText, matchingDevices, tagVocabulary, validateRule, type RuleHits } from './model';

const useStyles = makeStyles({
  head: { display: 'flex', alignItems: 'flex-start', padding: '22px 16px 14px 24px', borderBottom: `1px solid ${vt.surface2}` },
  titles: { flexGrow: 1, minWidth: 0 },
  title: { margin: 0, fontSize: '16px', fontWeight: tokens.fontWeightSemibold, color: vt.fg1 },
  sub: {
    fontFamily: tokens.fontFamilyMonospace,
    fontSize: '11px',
    color: vt.fg3,
    marginTop: '4px',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  section: { padding: '16px 24px', display: 'flex', flexDirection: 'column', rowGap: '12px', borderBottom: `1px solid ${vt.surface2}` },
  label: { fontSize: '11.5px', fontWeight: tokens.fontWeightSemibold, color: vt.fg2 },
  // Field labels and the action buttons use the mockup's caption size, not Fluent's body size.
  fieldLabel: { fontSize: '11.5px', fontWeight: tokens.fontWeightSemibold, color: vt.fg2, paddingBottom: '2px' },
  action: { fontSize: '12.5px', minWidth: 0, paddingInline: '14px' },
  segmented: { display: 'grid', gridTemplateColumns: '1fr 1fr', columnGap: '8px' },
  seg: {
    height: '34px',
    borderRadius: tokens.borderRadiusCircular,
    border: `1px solid ${vt.surface2}`,
    backgroundColor: 'transparent',
    color: vt.fg4,
    fontWeight: tokens.fontWeightSemibold,
    fontSize: '12.5px',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    columnGap: '8px',
    cursor: 'pointer',
    ':focus-visible': { outline: `2px solid ${vt.brand}`, outlineOffset: '1px' },
  },
  allowOn: { color: vt.green, backgroundColor: vt.tintGreen, border: `1px solid ${vt.green}` },
  denyOn: { color: vt.red, backgroundColor: vt.tintRed, border: `1px solid ${vt.red}` },
  mono: { fontFamily: tokens.fontFamilyMonospace },
  match: { fontSize: '10.5px', color: vt.fg4, display: 'flex', columnGap: '10px', minWidth: 0 },
  matchNames: { fontFamily: tokens.fontFamilyMonospace, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  pair: { display: 'grid', gridTemplateColumns: '130px minmax(0, 1fr)', columnGap: '16px', '& input': { minWidth: 0 } },
  fill: { width: '100%', minWidth: 0 },
  headTools: { display: 'flex', alignItems: 'center', columnGap: '2px', flexShrink: 0 },
  toggleRow: { display: 'flex', alignItems: 'center', columnGap: '8px' },
  effect: {
    borderRadius: '8px',
    backgroundColor: vt.surface3,
    padding: '14px',
    display: 'flex',
    flexDirection: 'column',
    rowGap: '8px',
    fontSize: '10.5px',
    color: vt.fg4,
  },
  flow: { display: 'flex', alignItems: 'center', columnGap: '6px', fontFamily: tokens.fontFamilyMonospace, fontSize: '11px', color: vt.fg2 },
  flowEnd: { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  allowed: { color: vt.green, fontFamily: tokens.fontFamilyMonospace, fontSize: '11px' },
  denied: { color: vt.red, fontFamily: tokens.fontFamilyMonospace, fontSize: '11px' },
  actions: { display: 'flex', alignItems: 'center', columnGap: '10px', padding: '18px 24px', '& button': { whiteSpace: 'nowrap' } },
  danger: { color: vt.red, border: `1px solid ${vt.red}`, marginLeft: 'auto' },
});

const PROTOCOLS: { value: PolicyProtocol; label: string }[] = [
  { value: 'any', label: 'Any' },
  { value: 'tcp', label: 'TCP' },
  { value: 'udp', label: 'UDP' },
  { value: 'icmp', label: 'ICMP' },
];

const hasPorts = (p: PolicyProtocol) => p === 'tcp' || p === 'udp';

function EndpointField({
  label,
  value,
  error,
  options,
  devices,
  onChange,
}: {
  label: string;
  value: string;
  error?: string;
  options: string[];
  devices: Device[];
  onChange: (v: string) => void;
}) {
  const s = useStyles();
  const matches = error ? [] : matchingDevices(value, devices);
  return (
    <Field
      label={{ children: label, className: s.fieldLabel }}
      validationMessage={error}
      hint={
        error ? undefined : (
          <span className={s.match} data-testid={`${label.toLowerCase()}-matches`}>
            <span>{value === 'any' ? 'every device' : matchText(matches.length)}</span>
            {value !== 'any' && matches.length > 0 && (
              <span className={s.matchNames}>{matches.map((d) => d.name).join(', ')}</span>
            )}
          </span>
        )
      }
    >
      <Combobox
        freeform
        className={s.mono}
        value={value}
        selectedOptions={[value]}
        onChange={(e) => onChange(e.target.value.trim())}
        onOptionSelect={(_e, data) => data.optionValue && onChange(data.optionValue)}
        aria-label={label}
      >
        {options.map((o) => (
          <Option key={o} value={o} text={o}>
            {o.startsWith('device:') ? `${o} (${devices.find((d) => `device:${d.id}` === o)?.name ?? '?'})` : o}
          </Option>
        ))}
      </Combobox>
    </Field>
  );
}

interface Props {
  /** The rule as stored; a new rule has an empty id. */
  rule: PolicyRule;
  /** 0-based; equal to the rule count for a rule not yet in the draft. */
  index: number;
  count: number;
  isNew: boolean;
  allRules: PolicyRule[];
  devices: Device[];
  busy: boolean;
  /** The rule's hit counter from GET /policy/status (as stored, not as edited). */
  hits: RuleHits;
  onSave: (rule: PolicyRule) => void;
  onDelete: () => void;
  onMove: (to: number) => void;
  onClose: () => void;
}

/** Mockup 05's inspector: one rule, edited locally, saved into the draft as a whole. */
export function RuleEditor({ rule, index, count, isNew, allRules, devices, busy, hits, onSave, onDelete, onMove, onClose }: Props) {
  const s = useStyles();
  const [form, setForm] = useState<PolicyRule>(rule);
  const errors = validateRule(form);
  const invalid = Object.keys(errors).length > 0;
  const set = <K extends keyof PolicyRule>(k: K, v: PolicyRule[K]) => setForm((f) => ({ ...f, [k]: v }));

  const options = useMemo(
    () => [
      'any',
      ...tagVocabulary(devices, allRules).map((t) => `tag:${t.tag}`),
      ...networksFromDevices(devices).map((n) => `net:${n.id}`),
      ...devices.map((d) => `device:${d.id}`),
    ],
    [devices, allRules],
  );

  const setAction = (a: PolicyAction) => set('action', a);
  const setProtocol = (p: PolicyProtocol) =>
    setForm((f) => ({
      ...f,
      protocol: p,
      // Ports only exist for TCP and UDP (CONTRACT 3.3).
      ...(hasPorts(p) ? {} : { sourcePorts: 'any', destinationPorts: 'any' }),
    }));

  const src = matchingDevices(form.source, devices);
  const dst = matchingDevices(form.destination, devices);
  const names = (list: Device[], e: string) =>
    e === 'any' ? 'any' : list.length ? list.map((d) => d.name).join(', ') : formatEndpoint(e);
  const service = form.protocol === 'any' ? 'any' : `${form.destinationPorts === 'any' ? '*' : form.destinationPorts}/${form.protocol}`;

  return (
    <>
      <div className={s.head}>
        <div className={s.titles}>
          <h2 className={s.title}>{isNew ? 'New rule' : `Rule ${index + 1}`}</h2>
          <div className={s.sub}>{form.comment || ' '}</div>
        </div>
        <div className={s.headTools}>
          {!isNew && (
            <>
              <Tooltip content="Move up" relationship="label">
                <Button
                  appearance="subtle"
                  shape="circular"
                  size="small"
                  icon={<ArrowUp16Regular />}
                  disabled={busy || index === 0}
                  onClick={() => onMove(index - 1)}
                />
              </Tooltip>
              <Tooltip content="Move down" relationship="label">
                <Button
                  appearance="subtle"
                  shape="circular"
                  size="small"
                  icon={<ArrowDown16Regular />}
                  disabled={busy || index >= count - 1}
                  onClick={() => onMove(index + 1)}
                />
              </Tooltip>
            </>
          )}
          <Tooltip content="Close" relationship="label">
            <Button appearance="subtle" shape="circular" size="small" icon={<Dismiss16Regular />} onClick={onClose} />
          </Tooltip>
        </div>
      </div>

      <div className={s.section}>
        <span className={s.label} id="rule-policy">
          Policy
        </span>
        {/* Two-up, never a dropdown: the one field where the wrong value is catastrophic. */}
        <div className={s.segmented} role="radiogroup" aria-labelledby="rule-policy">
          <button
            type="button"
            role="radio"
            aria-checked={form.action === 'allow'}
            className={mergeClasses(s.seg, form.action === 'allow' && s.allowOn)}
            onClick={() => setAction('allow')}
          >
            <Checkmark16Regular /> Allow
          </button>
          <button
            type="button"
            role="radio"
            aria-checked={form.action === 'deny'}
            className={mergeClasses(s.seg, form.action === 'deny' && s.denyOn)}
            onClick={() => setAction('deny')}
          >
            <Prohibited16Regular /> Deny
          </button>
        </div>

        <EndpointField
          label="Source"
          value={form.source}
          error={errors.source}
          options={options}
          devices={devices}
          onChange={(v) => set('source', v as PolicyEndpoint)}
        />
        <EndpointField
          label="Destination"
          value={form.destination}
          error={errors.destination}
          options={options}
          devices={devices}
          onChange={(v) => set('destination', v as PolicyEndpoint)}
        />

        <div className={s.pair}>
          <Field label={{ children: 'Protocol', className: s.fieldLabel }}>
            <Dropdown
              value={PROTOCOLS.find((p) => p.value === form.protocol)?.label}
              selectedOptions={[form.protocol]}
              onOptionSelect={(_e, data) => data.optionValue && setProtocol(data.optionValue as PolicyProtocol)}
              aria-label="Protocol"
              className={s.fill}
            >
              {PROTOCOLS.map((p) => (
                <Option key={p.value} value={p.value}>
                  {p.label}
                </Option>
              ))}
            </Dropdown>
          </Field>
          <Field
            label={{ children: 'Dst port', className: s.fieldLabel }}
            validationMessage={errors.destinationPorts}
            hint={!hasPorts(form.protocol) ? 'TCP or UDP only' : undefined}
          >
            <Input
              className={mergeClasses(s.mono, s.fill)}
              value={form.destinationPorts === 'any' ? '' : form.destinationPorts}
              placeholder="any"
              disabled={!hasPorts(form.protocol)}
              onChange={(_e, data) => set('destinationPorts', data.value.replace(/\s+/g, '') || 'any')}
              aria-label="Dst port"
            />
          </Field>
        </div>
        {errors.sourcePorts && <Field validationMessage={`Source ports: ${errors.sourcePorts}`} />}

        <Field label={{ children: 'Comment', className: s.fieldLabel }} validationMessage={errors.comment}>
          <Input value={form.comment} onChange={(_e, data) => set('comment', data.value)} aria-label="Comment" />
        </Field>
      </div>

      <div className={s.section}>
        <Switch
          checked={form.enabled}
          onChange={(_e, data) => set('enabled', data.checked)}
          label={form.enabled ? 'Enabled' : 'Disabled - kept in the policy, compiled as a comment'}
        />
      </div>

      <div className={s.section}>
        <span className={s.label}>Effect</span>
        <div className={s.effect} aria-label="Effect">
          <span className={s.flow}>
            <span className={s.flowEnd}>{names(src, form.source)}</span>
            <ChevronRight12Regular style={{ color: form.action === 'allow' ? vt.green : vt.red, flexShrink: 0 }} />
            <span className={s.flowEnd}>{names(dst, form.destination)}</span>
          </span>
          <span>
            <span className={form.action === 'allow' ? s.allowed : s.denied}>{service}</span>{' '}
            {form.enabled ? (form.action === 'allow' ? 'allowed' : 'dropped') : 'not enforced (disabled)'} ·{' '}
            {hitsText(hits)}
          </span>
        </div>
      </div>

      <div className={s.actions}>
        <Button
          appearance="primary"
          shape="circular"
          className={s.action}
          disabled={busy || invalid}
          onClick={() => onSave(form)}
        >
          Save to draft
        </Button>
        {!isNew && (
          <Button
            appearance="outline"
            shape="circular"
            className={mergeClasses(s.action, s.danger)}
            disabled={busy}
            onClick={onDelete}
          >
            Delete rule
          </Button>
        )}
      </div>
    </>
  );
}
